import BackHandIcon from "@mui/icons-material/BackHand";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { andonColors } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  selectWorkstationStatus,
  selectHandRaisedForWorkstation
} from "~/features/andon/andon.selectors";
import {
  selectGridModeByIdMap,
  selectPortOpenByIdMap
} from "~/features/andon/andon.slice";
import { figureAndonColorFromStatus } from "~/features/andon/andon.utils";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import CompactPortCardCompactView from "./PortCardCompactView";

type WorkstationCardCompactViewProps = { id: string };

export function WorkstationCardCompactView({
  id
}: WorkstationCardCompactViewProps) {
  const { t } = useTranslation();

  const portIsOpenById = useAppSelector(selectPortOpenByIdMap);
  const gridSystemModeById = useAppSelector(selectGridModeByIdMap);

  const { data: workstations } = useGetWorkstationsQuery();

  const workstation = workstations?.find(
    (workstation) => workstation.id === id
  );

  const workstationStatus = useAppSelector((state) =>
    selectWorkstationStatus(id)(state)
  );
  const handRaised = useAppSelector((state) =>
    selectHandRaisedForWorkstation(id)(state)
  );
  if (!workstation) return null;

  const isOpen = workstation.ports.some(
    (port) => portIsOpenById[port.portId.toString()]
  );

  const calculatedColors = figureAndonColorFromStatus({
    gridSystemMode:
      gridSystemModeById[workstation.autostoreGridId] ?? undefined,
    workstationStatus,
    workstationActive: workstation.status === "Active",
    workstationOpen: isOpen,
    handRaised: handRaised.raised
  });

  return (
    <Box
      component="figure"
      role="figure"
      aria-label={workstation.deviceId}
      id={id}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0%",
        minWidth: 0,
        border: "1px solid black",
        backgroundColor: calculatedColors.backgroundColor,
        boxSizing: "border-box",
        height: "100%",
        m: 0,
        gap: 0,
        justifyContent: "flex-start",
        alignItems: "center"
      }}
    >
      {handRaised.raised && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10
            }}
          >
            <BackHandIcon
              role="img"
              aria-label="hand raised"
              sx={{ fontSize: "4rem", color: andonColors.handRaisedYellow }}
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "black",
              opacity: 0.5
            }}
          />
        </>
      )}
      <Box
        sx={{
          p: 1,
          textAlign: "center",
          minHeight: "4rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography variant="h4">{workstation.deviceId}</Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 1
        }}
      >
        <Typography variant="h6">{t("ports")}</Typography>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-evenly",
            alignItems: "center",
            minWidth: 0
          }}
        >
          {workstation.ports.map((port) => (
            <CompactPortCardCompactView
              key={port.portId}
              portId={port.portId}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default WorkstationCardCompactView;
